import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  LogIn, Loader2, ShieldCheck, BarChart3, Map as MapIcon, Mail, Lock,
  CheckCircle2, FlaskConical,
} from 'lucide-react';
import ModonLogo from '../components/ModonLogo';
import SiteFooter from '../components/SiteFooter';
import LanguageSwitcher from '../components/LanguageSwitcher';

export default function Login() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === 'en' ? 'ltr' : 'rtl';
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setBusy(true);
    try {
      const u = await login(email, password);
      nav(
        u.role === 'admin'
          ? '/admin'
          : u.role === 'station_manager'
            ? '/station'
            : '/factory'
      );
    } catch (e) {
      const d = e?.response?.data?.detail;
      let msg = t('login.error');
      if (typeof d === 'string') msg = d;
      else if (Array.isArray(d) && d.length) msg = d.map((x) => x.msg || x).join('، ');
      setErr(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="modon-app-shell min-h-screen flex flex-col" dir={dir}>
      <div className="flex-1 grid md:grid-cols-5 min-h-[calc(100vh-72px)]">
        {/* ===== HERO PANEL ===== */}
        <div className="md:col-span-3 relative overflow-hidden order-1 flex items-center" style={{
          background: 'linear-gradient(135deg, #0f3457 0%, #1a4d80 45%, #2c6aa8 100%)'
        }}>
          {/* Language switcher floating top-end */}
          <div className={`absolute top-4 z-20 ${dir === 'rtl' ? 'left-4' : 'right-4'}`}>
            <LanguageSwitcher variant="dark"/>
          </div>
          <div className="absolute inset-0 opacity-[0.08]" style={{
            backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}/>
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(201,161,74,0.35) 0%, rgba(201,161,74,0) 70%)',
          }}/>
          <div className="absolute -bottom-32 -left-24 w-96 h-96 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(11,138,122,0.25) 0%, rgba(11,138,122,0) 70%)',
          }}/>
          <svg className="absolute bottom-0 left-0 right-0 w-full opacity-[0.18]" viewBox="0 0 1200 200" preserveAspectRatio="none" style={{ height: 180 }}>
            <path fill="#ffffff" d="M0,200 L0,140 L60,140 L60,90 L90,90 L90,140 L150,140 L150,60 L210,60 L210,140 L270,140 L270,100 L330,100 L330,40 L390,40 L390,140 L450,140 L450,80 L510,80 L510,140 L570,140 L570,50 L630,50 L630,140 L690,140 L690,100 L750,100 L750,140 L810,140 L810,30 L870,30 L870,140 L930,140 L930,90 L990,90 L990,140 L1050,140 L1050,70 L1110,70 L1110,140 L1200,140 L1200,200 Z"/>
          </svg>
          <div className="absolute bottom-0 right-0 left-0 h-1.5" style={{ background: 'linear-gradient(90deg, transparent, #c9a14a 25%, #e9c87b 50%, #c9a14a 75%, transparent)' }}/>

          <div className="relative z-10 px-8 md:px-14 lg:px-20 py-12 text-white max-w-2xl mx-auto md:mx-0">
            <ModonLogo size={88} className="mb-6 [&>div]:bg-white [&>div]:shadow-[0_12px_30px_-8px_rgba(0,0,0,0.4)]"/>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight leading-[1.15] mb-4">
              {t('login.hero.titleLine1')}
              <span className="block" style={{ color: '#e9c87b' }}>{t('login.hero.titleLine2')}</span>
            </h1>
            <p className="text-white/80 text-base md:text-[15px] leading-relaxed mb-6 max-w-xl">
              {t('login.hero.description')}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              <HeroFeature icon={ShieldCheck} text={t('login.hero.featureRisk')}/>
              <HeroFeature icon={BarChart3} text={t('login.hero.featureStats')}/>
              <HeroFeature icon={MapIcon} text={t('login.hero.featureMap')}/>
            </div>
            <div className="mt-8 flex items-center gap-4 text-xs text-white/70 flex-wrap">
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#e9c87b]"/> {t('login.hero.trustGHS')}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#e9c87b]"/> {t('login.hero.trustArabic')}</span>
              <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#e9c87b]"/> {t('login.hero.trustSources')}</span>
            </div>
          </div>
        </div>

        {/* ===== FORM PANEL ===== */}
        <div className="md:col-span-2 relative flex items-center justify-center p-6 md:p-10 order-2"
          style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 50%, #fef9ec 100%)' }}
        >
          <div className="w-full max-w-md">
            <div className="md:hidden flex items-center justify-between mb-4">
              <ModonLogo size={56}/>
              <LanguageSwitcher/>
            </div>

            <div className="bg-white rounded-2xl border border-slate-200/70 p-7 shadow-[0_24px_60px_-20px_rgba(15,55,100,0.35),0_8px_20px_-10px_rgba(15,55,100,0.18)] relative overflow-hidden">
              <div className="absolute top-0 right-0 left-0 h-1 bg-gradient-to-l from-transparent via-[#c9a14a] to-transparent"/>

              <div className="flex items-center justify-between mb-5">
                <div>
                  <h2 className="text-2xl font-extrabold text-[#0f3457] tracking-tight">{t('login.title')}</h2>
                  <p className="text-xs text-slate-500 mt-0.5">{t('login.subtitle')}</p>
                </div>
                <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white shadow-[inset_0_-3px_0_rgba(0,0,0,0.2),0_6px_14px_-3px_rgba(15,55,100,0.4)]" style={{ background: 'linear-gradient(135deg, #1a4d80, #0f3457)' }}>
                  <LogIn size={20}/>
                </div>
              </div>

              <form onSubmit={submit} className="space-y-3.5">
                <div>
                  <Label className="text-xs font-bold text-slate-700 mb-1.5 block">{t('login.email')}</Label>
                  <div className="relative">
                    <Mail size={16} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${dir === 'rtl' ? 'right-3' : 'left-3'}`}/>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      placeholder="name@modon.sa"
                      className={`${dir === 'rtl' ? 'pr-9' : 'pl-9'} h-11 bg-slate-50/60 border-slate-200 focus:bg-white focus:border-[#1a4d80] transition-colors`}
                      data-testid="login-email-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold text-slate-700 mb-1.5 block">{t('login.password')}</Label>
                  <div className="relative">
                    <Lock size={16} className={`absolute top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none ${dir === 'rtl' ? 'right-3' : 'left-3'}`}/>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      placeholder="••••••••"
                      className={`${dir === 'rtl' ? 'pr-9' : 'pl-9'} h-11 bg-slate-50/60 border-slate-200 focus:bg-white focus:border-[#1a4d80] transition-colors`}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                {err && (
                  <div className="text-red-700 text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="login-error">
                    {err}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={busy}
                  className="w-full h-11 text-base font-bold shadow-[inset_0_-3px_0_rgba(0,0,0,0.18),0_8px_22px_-6px_rgba(15,55,100,0.5)] hover:shadow-[inset_0_-3px_0_rgba(0,0,0,0.18),0_12px_28px_-6px_rgba(15,55,100,0.6)] transition-all"
                  style={{ background: 'linear-gradient(135deg, #1a4d80, #0f3457)' }}
                  data-testid="login-submit-btn"
                >
                  {busy ? <Loader2 className="animate-spin mx-2" size={18}/> : <LogIn size={18} className="mx-2"/>}
                  {busy ? t('login.submitting') : t('login.submit')}
                </Button>
              </form>

              <div className="relative my-4">
                <div className="absolute inset-0 flex items-center"><span className="w-full border-t border-slate-200"/></div>
                <div className="relative flex justify-center text-[11px]"><span className="bg-white px-3 text-slate-400 font-semibold">{t('login.or')}</span></div>
              </div>

              <Link to="/register" className="block" data-testid="register-link">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-11 border-[#1a4d80]/30 text-[#1a4d80] hover:bg-[#1a4d80]/5 hover:border-[#1a4d80] font-bold"
                >
                  <FlaskConical size={16} className="mx-2"/>
                  {t('login.registerLink')}
                </Button>
              </Link>
            </div>

            <p className="text-center text-[11px] text-slate-400 mt-4">
              {t('login.terms')}
            </p>
          </div>
        </div>
      </div>
      <SiteFooter/>
    </div>
  );
}

function HeroFeature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/15 rounded-xl px-3 py-2.5 text-xs text-white font-semibold hover:bg-white/15 hover:border-[#e9c87b]/40 hover:-translate-y-0.5 transition-all duration-300">
      <div className="w-7 h-7 rounded-lg flex items-center justify-center text-[#0f3457]" style={{ background: 'linear-gradient(135deg, #e9c87b, #c9a14a)' }}>
        <Icon size={14}/>
      </div>
      <span>{text}</span>
    </div>
  );
}
